/**
 * Stellies custom global interactions.
 * Owns the custom mega menu and cart/variant-aware PDP delivery components.
 * Initialization is idempotent and reruns for Shopify section loads without duplicating listeners.
 */
(function () {
  'use strict';

  function setDesktopMegaState(item, open) {
    const trigger = item.querySelector('[data-custom-mega-trigger]');
    item.classList.toggle('is-custom-mega-open', open);
    item.setAttribute('aria-expanded', String(open));
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
  }

  function cancelDesktopMegaClose(item) {
    if (!item.customMegaCloseTimer) return;
    window.clearTimeout(item.customMegaCloseTimer);
    item.customMegaCloseTimer = null;
  }

  function scheduleDesktopMegaClose(item) {
    cancelDesktopMegaClose(item);
    item.customMegaCloseTimer = window.setTimeout(function () {
      item.customMegaCloseTimer = null;
      if (!item.matches(':hover') && !item.contains(document.activeElement)) {
        setDesktopMegaState(item, false);
      }
    }, 150);
  }

  function closeDesktopMegas(scope, except) {
    scope.querySelectorAll('[data-custom-mega-item].is-custom-mega-open').forEach(function (item) {
      if (item !== except) {
        cancelDesktopMegaClose(item);
        setDesktopMegaState(item, false);
      }
    });
  }

  function initDesktopHeader(header) {
    if (!header || header.dataset.customHeaderInitialized === 'true') return;
    header.dataset.customHeaderInitialized = 'true';

    header.querySelectorAll('[data-custom-mega-item]').forEach(function (item) {
      const trigger = item.querySelector('[data-custom-mega-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', function () {
        if (window.matchMedia('(min-width: 981px)').matches) {
          cancelDesktopMegaClose(item);
          closeDesktopMegas(header, item);
          setDesktopMegaState(item, true);
        }
      });

      item.addEventListener('mouseleave', function () {
        scheduleDesktopMegaClose(item);
      });

      item.addEventListener('focusin', function () {
        cancelDesktopMegaClose(item);
        closeDesktopMegas(header, item);
        setDesktopMegaState(item, true);
      });

      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) setDesktopMegaState(item, false);
      });

      item.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        cancelDesktopMegaClose(item);
        setDesktopMegaState(item, false);
        trigger.focus();
      });
    });
  }

  function syncProductWishlist(productSection) {
    const proxy = productSection.querySelector('[data-wishlist-proxy]');
    const wishlist = productSection.querySelector('.th_prd_wl_btn');
    if (!proxy || !wishlist) return;

    const appBlock = wishlist.closest('.shopify-app-block') || wishlist;
    if (!appBlock.hasAttribute('data-wishlist-source')) {
      appBlock.setAttribute('data-wishlist-source', '');
    }

    const sourceAction = wishlist.querySelector('.th_wl_button, .th_wl_button_after');
    if (!sourceAction) return;

    const sourceText = sourceAction.querySelector('.th_wl_text');
    const label = sourceText && sourceText.textContent.trim() || 'Add to Wishlist';
    const isSaved = sourceAction.classList.contains('th_wl_button_after');

    proxy.classList.toggle('is-saved', isSaved);
    proxy.setAttribute('aria-pressed', String(isSaved));
    proxy.setAttribute('aria-label', label);
    proxy.setAttribute('title', label);

    if (appBlock.dataset.customWishlistSourceObserved !== 'true') {
      appBlock.dataset.customWishlistSourceObserved = 'true';
      const sourceObserver = new MutationObserver(function () {
        syncProductWishlist(productSection);
      });
      sourceObserver.observe(appBlock, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  }

  function bindWishlistProxy(productSection) {
    const proxy = productSection.querySelector('[data-wishlist-proxy]');
    if (!proxy || proxy.dataset.customWishlistProxyBound === 'true') return;

    proxy.dataset.customWishlistProxyBound = 'true';
    proxy.addEventListener('click', function () {
      const sourceAction = productSection.querySelector('.th_prd_wl_btn .th_wl_button, .th_prd_wl_btn .th_wl_button_after');
      if (!sourceAction) return;

      sourceAction.click();
      window.setTimeout(function () { syncProductWishlist(productSection); }, 100);
      window.setTimeout(function () { syncProductWishlist(productSection); }, 500);
    });
  }

  function initProductWishlist(productSection) {
    if (!productSection) return;
    bindWishlistProxy(productSection);
    syncProductWishlist(productSection);

    if (productSection.dataset.customWishlistObserver === 'true') return;
    productSection.dataset.customWishlistObserver = 'true';
    const observer = new MutationObserver(function () {
      bindWishlistProxy(productSection);
      syncProductWishlist(productSection);
    });
    observer.observe(productSection, {childList: true, subtree: true});
  }

  /**
   * Product card wishlist.
   * Wishlist Club injects its button into the .th_wl_col_btn placeholder the card
   * renders. That placeholder is hidden and the theme's heart proxies to it, so the
   * card keeps its own control while the app stays the source of truth, exactly as
   * the PDP proxy above does with .th_prd_wl_btn.
   */
  function cardWishlistAction(card) {
    const source = card.querySelector('[data-wishlist-card-source]');
    if (!source) return null;
    return source.querySelector('.th_wl_button, .th_wl_button_after, button, a');
  }

  function syncCardWishlist(card) {
    const proxy = card.querySelector('[data-wishlist-card]');
    const action = cardWishlistAction(card);
    if (!proxy || !action) return;

    const isSaved = action.classList.contains('th_wl_button_after') ||
      action.closest('.th_wl_button_after') !== null;

    proxy.classList.toggle('is-saved', isSaved);
    proxy.setAttribute('aria-pressed', String(isSaved));
  }

  function initCardWishlist(card) {
    const source = card.querySelector('[data-wishlist-card-source]');
    if (!source) return;

    // The app mounts and re-renders its button asynchronously, so the heart follows
    // whatever it does rather than assuming it landed.
    if (source.dataset.wishlistCardObserved !== 'true') {
      source.dataset.wishlistCardObserved = 'true';
      const observer = new MutationObserver(function () { syncCardWishlist(card); });
      observer.observe(source, { attributes: true, childList: true, subtree: true });
    }

    syncCardWishlist(card);
  }

  function scanCardWishlists(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-wishlist-card]').forEach(function (proxy) {
      const card = proxy.closest('.product--grid-image') || proxy.parentElement;
      if (card) initCardWishlist(card);
    });
  }

  // Delegated, so cards that arrive later - carousels, infinite scroll, quick
  // rendered sections - work without waiting to be scanned.
  document.addEventListener('click', function (event) {
    const proxy = event.target.closest && event.target.closest('[data-wishlist-card]');
    if (!proxy) return;

    const card = proxy.closest('.product--grid-image') || proxy.parentElement;
    if (!card) return;

    const action = cardWishlistAction(card);
    if (!action) return;

    event.preventDefault();
    action.click();
    window.setTimeout(function () { syncCardWishlist(card); }, 100);
    window.setTimeout(function () { syncCardWishlist(card); }, 500);
  });

  function addBusinessDays(date, days) {
    const result = new Date(date);
    let remaining = days;

    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) remaining -= 1;
    }

    return result;
  }

  function formatDeliveryDate(date) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-ZA', {
      day: 'numeric',
      month: 'short'
    }).format(date);
  }

  function setDeliveryRange(block, type, minimumDays, maximumDays) {
    const target = block.querySelector('[data-dynamic-shipping-range="' + type + '"]');
    if (!target) return;

    const today = new Date();
    const earliestDays = Math.min(minimumDays, maximumDays);
    const latestDays = Math.max(minimumDays, maximumDays);
    target.textContent = formatDeliveryDate(addBusinessDays(today, earliestDays)) +
      '–' + formatDeliveryDate(addBusinessDays(today, latestDays));
  }

  function formatRand(cents) {
    return 'R' + Math.ceil(cents / 100).toLocaleString('en-US');
  }

  /** Combined standard-delivery and live Shopify pickup widget. */
  class ProductDeliveryPickup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.productContext = this.closest('.product-template') || this.parentElement;
      this.pickupTime = this.querySelector('[data-delivery-pickup-time]');
      this.pickupPrice = this.querySelector('[data-delivery-pickup-price]');
      this.pickupAction = this.querySelector('[data-delivery-pickup-action]');
      this.pickupDrawer = this.querySelector('[data-delivery-pickup-drawer]');
      this.handleProductChange = this.handleProductChange.bind(this);

      if (this.productContext) this.productContext.addEventListener('change', this.handleProductChange);
      this.updateCartTotal(this.dataset.cartTotal);
      this.loadPickup(this.dataset.variantId);
    }

    disconnectedCallback() {
      if (this.productContext) this.productContext.removeEventListener('change', this.handleProductChange);
      if (this.pickupRequest) this.pickupRequest.abort();
      if (this.variantCheckFrame) window.cancelAnimationFrame(this.variantCheckFrame);
      delete this.dataset.initialized;
    }

    handleProductChange(event) {
      if (event.target.matches('.formVariantId')) {
        this.loadPickup(event.target.value);
        return;
      }

      if (!event.target.closest('.js-variant-selector')) return;
      if (this.variantCheckFrame) window.cancelAnimationFrame(this.variantCheckFrame);
      this.variantCheckFrame = window.requestAnimationFrame(() => {
        const variantInput = this.productContext.querySelector('.formVariantId');
        const variantId = variantInput && variantInput.value;
        if (variantId && variantId !== this.currentVariantId) {
          this.loadPickup(variantId);
          return;
        }

        const addButton = this.productContext.querySelector('.AddtoCart');
        if (!variantId || (addButton && addButton.disabled)) this.showPickupUnavailable();
      });
    }

    updateCartTotal(cartTotal) {
      const threshold = Number(this.dataset.freeShippingThreshold) || 100000;
      const total = Math.max(0, Number(cartTotal) || 0);
      const message = this.querySelector('[data-delivery-pickup-threshold]');
      const isQualified = total >= threshold;

      this.dataset.cartTotal = String(total);
      this.classList.toggle('is-free-shipping-qualified', isQualified);
      if (!message) return;

      const remainingText = this.dataset.remainingText || '{amount_remaining} to free shipping';
      const qualifiedText = this.dataset.qualifiedText || 'Free shipping unlocked';
      message.textContent = isQualified
        ? qualifiedText
        : remainingText.replace('{amount_remaining}', formatRand(threshold - total));
    }

    setPickupState(message, showPrice, showAction) {
      if (this.pickupTime) this.pickupTime.textContent = message;
      if (this.pickupPrice) this.pickupPrice.hidden = !showPrice;
      if (this.pickupAction) this.pickupAction.hidden = !showAction;
      if (!showAction && this.pickupAction) this.pickupAction.removeAttribute('popovertarget');
      if (!showAction && this.pickupDrawer) this.pickupDrawer.replaceChildren();
    }

    showPickupUnavailable() {
      this.setPickupState(this.dataset.pickupUnavailableText || 'Unavailable for this option', false, false);
    }

    showPickupError() {
      this.setPickupState(this.dataset.pickupErrorText || 'Pickup availability temporarily unavailable', false, false);
    }

    loadPickup(variantId) {
      if (!variantId || variantId === this.currentVariantId && this.pickupRequest) return;
      this.currentVariantId = String(variantId || '');
      if (this.pickupRequest) this.pickupRequest.abort();
      if (!variantId) {
        this.showPickupUnavailable();
        return;
      }

      const request = new AbortController();
      this.pickupRequest = request;
      this.dataset.variantId = this.currentVariantId;
      const baseUrl = (this.dataset.baseUrl || '/').replace(/\/?$/, '/');
      const endpoint = baseUrl + 'variants/' + encodeURIComponent(variantId) + '/?section_id=pickup-availability';
      this.setPickupState(this.dataset.pickupLoadingText || 'Checking availability…', false, false);

      fetch(endpoint, { signal: request.signal, headers: { 'Accept': 'text/html' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Pickup availability request failed.');
          return response.text();
        })
        .then((html) => {
          if (request.signal.aborted || this.currentVariantId !== String(variantId)) return;
          this.pickupRequest = null;
          this.renderPickupResponse(html, variantId);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          if (this.currentVariantId === String(variantId)) {
            this.pickupRequest = null;
            this.showPickupError();
          }
        });
    }

    renderPickupResponse(html, variantId) {
      if (!html.trim()) {
        this.showPickupError();
        return;
      }

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const availability = documentFragment.querySelector('[data-pick-up-available]');
      const immediatePickupTime = availability && availability.querySelector('.store-availability-information__stock');
      const availableStoreStatus = availability && Array.from(availability.querySelectorAll('.store-availability-list__stock')).find(function (status) {
        return status.querySelector('.icon--apollo-checkmark');
      });

      if (!availability || availability.dataset.pickUpAvailable !== 'true' || (!immediatePickupTime && !availableStoreStatus)) {
        this.showPickupUnavailable();
        return;
      }

      let pickupTime = immediatePickupTime ? immediatePickupTime.textContent : availableStoreStatus.textContent;
      pickupTime = pickupTime.replace(/\s+/g, ' ').trim().replace(/^[^,]+,\s*/, '');
      if (!pickupTime) pickupTime = this.dataset.pickupAvailableText || 'Available for pickup';

      const drawer = availability.querySelector('aside[popover]');
      if (!drawer) {
        this.setPickupState(pickupTime, true, false);
        return;
      }

      const drawerId = this.id + '-stores-' + variantId;
      drawer.id = drawerId;
      drawer.querySelectorAll('[popovertarget]').forEach(function (trigger) {
        trigger.setAttribute('popovertarget', drawerId);
      });
      drawer.querySelectorAll('[data-wau-slideout-content]').forEach(function (content) {
        content.setAttribute('data-wau-slideout-content', drawerId);
      });

      this.pickupDrawer.replaceChildren(drawer);
      this.pickupAction.setAttribute('popovertarget', drawerId);
      this.pickupAction.setAttribute('popovertargetaction', 'show');
      this.pickupAction.textContent = this.dataset.pickupActionText || 'View store availability';
      this.setPickupState(pickupTime, true, true);
    }
  }

  if (!customElements.get('product-delivery-pickup')) {
    customElements.define('product-delivery-pickup', ProductDeliveryPickup);
  }

  function updateDeliveryWidget(block, cartTotal) {
    const threshold = Number(block.dataset.freeShippingThreshold) || 100000;
    const total = Math.max(0, Number(cartTotal) || 0);
    const percentage = Math.min(100, Math.round((total / threshold) * 100));
    const progress = block.querySelector('[role="progressbar"]');
    const progressBar = block.querySelector('.dynamic-shipping__progress-bar');
    const message = block.querySelector('[data-dynamic-shipping-threshold]');

    block.dataset.cartTotal = String(total);
    if (progress) progress.setAttribute('aria-valuenow', String(percentage));
    if (progressBar) progressBar.style.setProperty('--dynamic-shipping-progress', percentage + '%');
    if (message) {
      const remainingText = block.dataset.remainingText || 'Spend {amount_remaining} more to qualify for free shipping.';
      const qualifiedText = block.dataset.qualifiedText || 'Your order qualifies for free shipping.';
      message.textContent = total >= threshold
        ? qualifiedText
        : remainingText.replace('{amount_remaining}', formatRand(threshold - total));
    }
  }

  function initDeliveryWidget(block) {
    if (!block) return;
    setDeliveryRange(block, 'express', Number(block.dataset.expressMinLeadTime) || 2, Number(block.dataset.expressMaxLeadTime) || 3);
    setDeliveryRange(block, 'standard', Number(block.dataset.standardMinLeadTime) || 3, Number(block.dataset.standardMaxLeadTime) || 4);
    updateDeliveryWidget(block, block.dataset.cartTotal);
  }

  function updateAllDeliveryWidgets(cartTotal) {
    document.querySelectorAll('[data-dynamic-shipping]').forEach(function (block) {
      updateDeliveryWidget(block, cartTotal);
    });
    document.querySelectorAll('product-delivery-pickup').forEach(function (widget) {
      if (typeof widget.updateCartTotal === 'function') widget.updateCartTotal(cartTotal);
    });
  }

  function refreshDeliveryCartState() {
    return fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
      headers: {'Accept': 'application/json'}
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        if (cart) updateAllDeliveryWidgets(cart.total_price);
      })
      .catch(function () {
        // Server-rendered cart state remains available when the request fails.
      });
  }

  function observeAjaxCart() {
    if (window.customDeliveryCartObserved) return;
    window.customDeliveryCartObserved = true;

    const ajaxCart = window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart;
    if (ajaxCart && typeof ajaxCart.updateView === 'function') {
      const originalUpdateView = ajaxCart.updateView;
      ajaxCart.updateView = function (config, cart) {
        if (cart && typeof cart.total_price !== 'undefined') updateAllDeliveryWidgets(cart.total_price);
        return originalUpdateView.apply(this, arguments);
      };
    }

    let cartRefreshTimer;
    const cartObserver = new MutationObserver(function () {
      window.clearTimeout(cartRefreshTimer);
      cartRefreshTimer = window.setTimeout(refreshDeliveryCartState, 100);
    });
    document.querySelectorAll('.js-cart-count, .js-ajax-cart-content').forEach(function (cartElement) {
      cartObserver.observe(cartElement, {childList: true, subtree: true, characterData: true});
    });

    refreshDeliveryCartState();
  }

  /**
   * Swaps the pre-order stock countdown to the selected variant's remaining
   * units. theme.js writes the chosen variant into `.formVariantId` and fires a
   * bubbling change event on it, which is the same hook the delivery widget uses.
   */
  function updatePreorderStock(widget, variantId) {
    const message = widget.querySelector('[data-custom-preorder-stock-message]');
    if (!message) return;

    let inventory;
    try {
      inventory = JSON.parse(widget.dataset.customPreorderStockInventory || '{}');
    } catch (error) {
      inventory = {};
    }

    const remaining = inventory[String(variantId)];
    const threshold = Number(widget.dataset.customPreorderStockThreshold) || 0;

    if (typeof remaining !== 'number' || remaining <= 0) {
      widget.hidden = true;
      return;
    }

    const template = remaining === 1
      ? widget.dataset.customPreorderStockMessageOne
      : widget.dataset.customPreorderStockMessageOther;

    message.textContent = String(template || '').replace('{{ count }}', remaining);
    widget.classList.toggle('custom-preorder-stock--low', remaining <= threshold);
    widget.hidden = false;

    const bar = widget.querySelector('[data-custom-preorder-stock-bar]');
    const peak = Number(widget.dataset.customPreorderStockPeak) || 1;
    if (bar) bar.style.width = Math.min(100, Math.round((remaining / peak) * 100)) + '%';
  }

  function initPreorderStock(widget) {
    if (!widget || widget.dataset.customPreorderStockInitialized === 'true') return;
    widget.dataset.customPreorderStockInitialized = 'true';

    const productContext = widget.closest('.product-template');
    if (!productContext) return;

    productContext.addEventListener('change', function (event) {
      if (event.target.matches('.formVariantId')) {
        updatePreorderStock(widget, event.target.value);
        return;
      }

      // theme.js only rewrites `.formVariantId` for combinations that resolve to
      // a real variant, so an unavailable combination would otherwise leave the
      // previous variant's count on screen.
      if (!event.target.closest('.js-variant-selector')) return;
      window.requestAnimationFrame(function () {
        const addButton = productContext.querySelector('.AddtoCart');
        if (addButton && addButton.disabled) widget.hidden = true;
      });
    });
  }

  const SECONDS_PER_DAY = 86400;

  /**
   * Whole days from now until `timestamp`, counted in calendar days rather than
   * elapsed 24-hour blocks so "in 19 days" matches what a calendar would say.
   */
  function daysUntil(timestamp) {
    const target = new Date(timestamp * 1000);
    const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((targetMidnight - todayMidnight) / (SECONDS_PER_DAY * 1000));
  }

  /**
   * Rewrites the timeline's relative distances on the client. The page is
   * rendered with them already filled in, but a cached response could otherwise
   * keep serving the day count that was true when it was cached.
   */
  function refreshRelativeDate(element) {
    const timestamp = Number(element.dataset.customRelativeDate);
    const prefix = element.dataset.customRelativeDatePrefix || '';
    if (!timestamp) return;

    const days = daysUntil(timestamp);
    let phrase;
    if (days > 1) {
      phrase = 'in ' + days + ' days';
    } else if (days === 1) {
      phrase = 'tomorrow';
    } else if (days === 0) {
      phrase = 'today';
    } else {
      phrase = '';
    }

    element.textContent = phrase ? prefix + ' ' + phrase : prefix;
  }

  function padClockUnit(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * Ticks the "pre-order closes in" clock once a second, and swaps it for the
   * closed message when the deadline passes.
   */
  function initPreorderCountdown(widget) {
    if (!widget || widget.dataset.customPreorderCountdownInitialized === 'true') return;
    widget.dataset.customPreorderCountdownInitialized = 'true';

    const clock = widget.querySelector('[data-custom-preorder-countdown-clock]');
    const deadline = Number(widget.dataset.customPreorderCountdownDeadline);
    if (!clock || !deadline) return;

    const tick = function () {
      const remaining = deadline - Math.floor(Date.now() / 1000);

      if (remaining <= 0) {
        clock.textContent = widget.dataset.customPreorderCountdownEnded || '';
        const label = widget.querySelector('.custom-preorder-countdown__label');
        if (label) label.remove();
        window.clearInterval(widget.customPreorderCountdownTimer);
        return;
      }

      const days = Math.floor(remaining / SECONDS_PER_DAY);
      const hours = Math.floor((remaining % SECONDS_PER_DAY) / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;

      clock.textContent = days + 'd ' + padClockUnit(hours) + 'h ' +
        padClockUnit(minutes) + 'm ' + padClockUnit(seconds) + 's';
    };

    tick();
    widget.customPreorderCountdownTimer = window.setInterval(tick, 1000);
  }

  /**
   * theme.js cycles the buy button through "Adding"/"Added" and then restores
   * cart-config's shop-wide add-to-cart label onto it, which would replace a
   * section's own buy-button text. It re-parses #cart-config into a fresh config
   * object on every click, so pointing that object at this button's own label is
   * enough - and unlike restoring the label afterwards it can't race the reset,
   * whose timer starts when the cart request returns rather than on click.
   */
  function patchCustomAddToCartLabel() {
    if (window.customAddToCartLabelPatched) return;

    const ajaxCart = window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart;
    if (!ajaxCart || typeof ajaxCart.addToCart !== 'function') return;
    window.customAddToCartLabelPatched = true;

    const originalAddToCart = ajaxCart.addToCart;
    ajaxCart.addToCart = function (addToCartForm, formContext, config) {
      const button = addToCartForm && addToCartForm.querySelector('[data-custom-add-to-cart-text]');
      if (button && config) config.add_to_cart = button.dataset.customAddToCartText;
      return originalAddToCart.apply(this, arguments);
    };
  }

  function initMobileSearchTrigger(trigger) {
    if (!trigger || trigger.dataset.mobileSearchInitialized === 'true') return;
    trigger.dataset.mobileSearchInitialized = 'true';

    trigger.addEventListener('click', function (event) {
      const searchDrawer = document.getElementById(trigger.getAttribute('popovertarget'));
      const mobileDrawer = trigger.closest('[popover]');
      if (!searchDrawer || typeof searchDrawer.showPopover !== 'function') return;

      event.preventDefault();
      if (mobileDrawer && mobileDrawer.matches(':popover-open')) mobileDrawer.hidePopover();
      searchDrawer.showPopover();

      window.requestAnimationFrame(function () {
        const searchInput = searchDrawer.querySelector('input[type="search"], input[name="q"]');
        if (searchInput) searchInput.focus();
      });
    });
  }

  function initCustomMobileMenu(menu) {
    if (!menu || menu.dataset.customMobileMenuInitialized === 'true') return;
    menu.dataset.customMobileMenuInitialized = 'true';

    const slideout = menu.closest('[popover]');
    const panels = Array.from(menu.querySelectorAll('[data-custom-mobile-menu-panel]'));
    const rootPanel = panels.find(function (panel) { return panel.dataset.panelDepth === '0'; });
    if (!rootPanel) return;

    let panelStack = [rootPanel];
    const scrollPositions = new Map();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function panelScroll(panel) {
      return panel && panel.querySelector('[data-custom-mobile-menu-scroll]');
    }

    function syncPanelState() {
      panels.forEach(function (panel) {
        const stackIndex = panelStack.indexOf(panel);
        const isActive = stackIndex === panelStack.length - 1;
        const isBehind = stackIndex > -1 && !isActive;

        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-behind', isBehind);
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.inert = !isActive;
      });
    }

    function focusAfterTransition(element) {
      if (!element) return;
      window.setTimeout(function () { element.focus(); }, reduceMotion.matches ? 0 : 230);
    }

    function openPanel(targetPanel, trigger, moveFocus) {
      if (!targetPanel || panelStack[panelStack.length - 1] === targetPanel) return;

      const activePanel = panelStack[panelStack.length - 1];
      const activeScroll = panelScroll(activePanel);
      if (activeScroll) scrollPositions.set(activePanel.id, activeScroll.scrollTop);

      panelStack.push(targetPanel);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      const targetScroll = panelScroll(targetPanel);
      if (targetScroll) targetScroll.scrollTop = scrollPositions.get(targetPanel.id) || 0;
      syncPanelState();

      if (moveFocus !== false) {
        focusAfterTransition(targetPanel.querySelector('[data-custom-mobile-menu-back]'));
      }
    }

    function goBack(moveFocus) {
      if (panelStack.length <= 1) return;

      const leavingPanel = panelStack.pop();
      const triggerId = leavingPanel.dataset.triggerId;
      const trigger = triggerId && document.getElementById(triggerId);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      const parentPanel = panelStack[panelStack.length - 1];
      const parentScroll = panelScroll(parentPanel);
      syncPanelState();
      if (parentScroll) parentScroll.scrollTop = scrollPositions.get(parentPanel.id) || 0;
      if (moveFocus !== false) focusAfterTransition(trigger);
    }

    function resetMenu() {
      menu.querySelectorAll('[data-custom-mobile-menu-open]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      panels.forEach(function (panel) {
        const scroll = panelScroll(panel);
        if (scroll) scroll.scrollTop = 0;
      });
      scrollPositions.clear();
      panelStack = [rootPanel];
      syncPanelState();
    }

    function revealPanel(targetPanel) {
      if (!targetPanel) return;
      const path = [];
      let panel = targetPanel;

      while (panel && panel !== rootPanel) {
        path.unshift(panel);
        panel = document.getElementById(panel.dataset.parentPanel);
      }

      resetMenu();
      path.forEach(function (pathPanel) {
        const trigger = document.getElementById(pathPanel.dataset.triggerId);
        openPanel(pathPanel, trigger, false);
      });
    }

    menu.querySelectorAll('[data-custom-mobile-menu-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openPanel(document.getElementById(trigger.dataset.customMobileMenuOpen), trigger, true);
      });
    });

    menu.querySelectorAll('[data-custom-mobile-menu-back]').forEach(function (backButton) {
      backButton.addEventListener('click', function () { goBack(true); });
    });

    menu.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (slideout && slideout.matches(':popover-open') && typeof slideout.hidePopover === 'function') {
        slideout.hidePopover();
      } else {
        resetMenu();
      }
    });

    if (slideout) {
      slideout.addEventListener('toggle', function (event) {
        if (event.newState === 'closed' || !slideout.matches(':popover-open')) resetMenu();
      });
    }

    menu.customMobileMenuReveal = revealPanel;
    syncPanelState();
  }

  function initialize(scope) {
    const root = scope || document;
    if (root.matches && root.matches('[data-custom-header]')) initDesktopHeader(root);
    root.querySelectorAll && root.querySelectorAll('[data-custom-header]').forEach(initDesktopHeader);
    if (root.matches && root.matches('.product-template')) initProductWishlist(root);
    root.querySelectorAll && root.querySelectorAll('.product-template').forEach(initProductWishlist);
    scanCardWishlists(root);
    if (root.matches && root.matches('[data-dynamic-shipping]')) initDeliveryWidget(root);
    root.querySelectorAll && root.querySelectorAll('[data-dynamic-shipping]').forEach(initDeliveryWidget);
    if (root.matches && root.matches('[data-mobile-search-trigger]')) initMobileSearchTrigger(root);
    root.querySelectorAll && root.querySelectorAll('[data-mobile-search-trigger]').forEach(initMobileSearchTrigger);
    if (root.matches && root.matches('[data-custom-mobile-menu]')) initCustomMobileMenu(root);
    root.querySelectorAll && root.querySelectorAll('[data-custom-mobile-menu]').forEach(initCustomMobileMenu);
    if (root.matches && root.matches('[data-custom-preorder-stock]')) initPreorderStock(root);
    root.querySelectorAll && root.querySelectorAll('[data-custom-preorder-stock]').forEach(initPreorderStock);
    if (root.matches && root.matches('[data-custom-preorder-countdown]')) initPreorderCountdown(root);
    root.querySelectorAll && root.querySelectorAll('[data-custom-preorder-countdown]').forEach(initPreorderCountdown);
    root.querySelectorAll && root.querySelectorAll('[data-custom-relative-date]').forEach(refreshRelativeDate);
    patchCustomAddToCartLabel();
    observeAjaxCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialize(document); }, { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const countdowns = event.target.querySelectorAll
      ? event.target.querySelectorAll('[data-custom-preorder-countdown]')
      : [];
    countdowns.forEach(function (widget) {
      window.clearInterval(widget.customPreorderCountdownTimer);
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    const desktopItem = event.target.closest && event.target.closest('[data-custom-mega-item]');
    if (desktopItem) setDesktopMegaState(desktopItem, true);

    const mobileMenu = event.target.closest && event.target.closest('[data-custom-mobile-menu]');
    const mobilePanel = event.target.closest && event.target.closest('[data-custom-mobile-menu-panel]');
    if (mobileMenu && mobilePanel && typeof mobileMenu.customMobileMenuReveal === 'function') {
      mobileMenu.customMobileMenuReveal(mobilePanel);
    }
  });
})();

/**
 * Adds the crossed-out former shipping price after UpCart reports that free
 * shipping has been unlocked, and adds the 30-day returns/refunds message.
 * UpCart replaces its cart markup whenever the cart changes, so this observes
 * those replacements as well.
 */
(function initUpcartUnlockedShippingPrice() {
  if (window.__upcartUnlockedShippingPriceInitialized) return;
  window.__upcartUnlockedShippingPriceInitialized = true;

  const MESSAGE_SELECTOR = '.upcart-rewards-message, .upcart-public-rewards__message';
  const CART_BODY_SELECTOR = '.upcart-cart-body, .upcart-public-cart__body';
  const UNLOCKED_CLASS = 'upcart-free-shipping-unlocked';
  const POLICY_CLASS = 'upcart-policy-message';
  const STYLE_ID = 'upcart-unlocked-shipping-price-style';
  const observedRoots = new WeakSet();

  function addStyles(root) {
    if (root.querySelector(`#${STYLE_ID}`)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .${UNLOCKED_CLASS} > p {
        display: inline;
      }

      .${UNLOCKED_CLASS}::after {
        content: " R99";
        margin-left: 4px;
        text-decoration: line-through;
      }

      .${POLICY_CLASS} {
        font-size: 12px;
        line-height: 1.4;
        margin: 12px 16px;
        text-align: center;
      }

      .${POLICY_CLASS} a {
        color: inherit;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    `;
    (root === document ? document.head : root).appendChild(style);
  }

  function updateMessages(root) {
    root.querySelectorAll(MESSAGE_SELECTOR).forEach(function (message) {
      const isUnlocked = message.textContent
        .trim()
        .toLowerCase()
        .includes('free shipping unlocked');

      message.classList.toggle(UNLOCKED_CLASS, isUnlocked);
    });
  }

  function addPolicyMessage(root) {
    if (root.querySelector(`.${POLICY_CLASS}`)) return;

    const cartBody = root.querySelector(CART_BODY_SELECTOR);
    if (!cartBody) return;

    const message = document.createElement('p');
    const link = document.createElement('a');
    message.className = POLICY_CLASS;
    link.href = '/pages/guarantees-and-returns';
    link.textContent = 'Free returns & refunds within 30 days. Exclusions apply.';
    message.appendChild(link);
    cartBody.appendChild(message);
  }

  function observeRoot(root) {
    addStyles(root);
    updateMessages(root);
    addPolicyMessage(root);

    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    new MutationObserver(function () {
      updateMessages(root);
      addPolicyMessage(root);
    }).observe(root === document ? document.body : root, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function findUpcartRoot() {
    observeRoot(document);

    const upcartShadowRoot = document.getElementById('upCart')?.shadowRoot;
    if (upcartShadowRoot) observeRoot(upcartShadowRoot);
  }

  function initialize() {
    findUpcartRoot();

    new MutationObserver(findUpcartRoot).observe(document.body, {
      childList: true,
      subtree: true
    });

    // A shadow root can be attached without producing a document mutation.
    const shadowRootTimer = window.setInterval(function () {
      const upcartShadowRoot = document.getElementById('upCart')?.shadowRoot;
      if (!upcartShadowRoot) return;

      observeRoot(upcartShadowRoot);
      window.clearInterval(shadowRootTimer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
(function initializeFitCheckCentering() {
  if (window.__fitCheckCenteringInitialized) return;
  window.__fitCheckCenteringInitialized = true;

  const TRIGGER_SELECTOR = '.fit-check-container #fit-check-trigger, .fit-check-container #fit-check-size-trigger';

  // Both triggers render as matching outlined buttons.
  const TRIGGER_STYLES = {
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    'text-align': 'center',
    gap: '8px',
    width: '100%',
    'box-sizing': 'border-box',
    'min-height': '48px',
    padding: '12px 24px',
    border: '1px solid #081738',
    color: '#081738',
    'background-color': 'transparent',
    'box-shadow': 'none',
    'line-height': 'normal',
    // The icon is a sibling flex item, so the label must not wrap around it.
    'white-space': 'nowrap'
  };

  // The app injects a <button> inside the size <button>. Left alone that nested
  // button picks up the theme's button rules, stretches to fill the row and
  // pushes the label onto a second line. Pin it to its own content instead.
  const ICON_STYLES = {
    display: 'inline-flex',
    'align-items': 'center',
    'justify-content': 'center',
    flex: '0 0 auto',
    width: 'auto',
    height: 'auto',
    'min-width': '0',
    'min-height': '0',
    padding: '0',
    margin: '0',
    border: 'none',
    'background-color': 'transparent',
    'box-shadow': 'none',
    'line-height': '0',
    color: '#081738'
  };

  // Only the gap between the two triggers belongs here. Spacing between this
  // block and the rest of the product info column is owned by the section
  // stylesheet (assets/section-main-product.css), so it stays in one place.
  const SPACING = {
    'fit-check-trigger': { margin: '0 0 8px 0' },
    'fit-check-size-trigger': { margin: '0' }
  };

  function setStyles(el, styles) {
    Object.entries(styles).forEach(([prop, value]) => {
      if (el.style.getPropertyValue(prop) !== value) {
        el.style.setProperty(prop, value, 'important');
      }
    });
  }

  function fixTrigger(trigger) {
    setStyles(trigger, TRIGGER_STYLES);

    const spacing = SPACING[trigger.id];
    if (spacing) setStyles(trigger, spacing);

    trigger.querySelectorAll('button, a, [role="button"]').forEach((icon) => {
      setStyles(icon, ICON_STYLES);
    });
  }

  const watchedTriggers = new WeakSet();

  function watchTrigger(trigger) {
    if (watchedTriggers.has(trigger)) return;
    watchedTriggers.add(trigger);
    fixTrigger(trigger);

    new MutationObserver(() => fixTrigger(trigger)).observe(trigger, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true
    });
  }

  function scanForTriggers() {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach(watchTrigger);
  }

  function initialize() {
    scanForTriggers();
    new MutationObserver(() => window.requestAnimationFrame(scanForTriggers))
      .observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

(function initDeferredProductMedia() {
  if (window.__deferredProductMediaInitialized) return;
  window.__deferredProductMediaInitialized = true;

  const GALLERY = '.product__section-images';
  const DEFERRED = 'img.js-deferred-media';

  // product-static-gallery.liquid ships every slide after the first with its
  // src and srcset parked in data- attributes. Flickity stacks all the cells
  // at one position, so the browser treats them as in-viewport the moment it
  // initialises and loading="lazy" stops deferring anything. Hand the sources
  // back the first time the gallery is actually used.
  function hydrate(gallery) {
    const deferred = gallery.querySelectorAll(DEFERRED);
    if (!deferred.length) return;

    deferred.forEach(function (img) {
      const srcset = img.getAttribute('data-srcset');
      const src = img.getAttribute('data-src');

      if (srcset) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-srcset');
      }
      if (src) {
        img.setAttribute('src', src);
        img.removeAttribute('data-src');
      }

      img.classList.remove('js-deferred-media');
    });
  }

  function watch(gallery) {
    if (gallery.dataset.deferredMediaBound === 'true') return;
    gallery.dataset.deferredMediaBound = 'true';

    const run = function () { hydrate(gallery); };
    const events = ['pointerdown', 'touchstart', 'mouseenter', 'focusin', 'keydown'];
    events.forEach(function (type) {
      gallery.addEventListener(type, run, { once: true, passive: true, capture: true });
    });

    // The slide can also change without touching the gallery - picking a
    // variant moves it. Watch for selection landing anywhere but the first
    // cell and hydrate then.
    const carousel = gallery.querySelector('.carousel-main');
    if (!carousel) return;

    const observer = new MutationObserver(function () {
      const selected = carousel.querySelector('.carousel-cell.is-selected');
      if (selected && selected !== carousel.querySelector('.carousel-cell')) {
        hydrate(gallery);
        observer.disconnect();
      }
    });
    observer.observe(carousel, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  function scan() {
    document.querySelectorAll(GALLERY).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
})();

/**
 * Instant add to cart for single variant product cards.
 * snippets/product-grid-add.liquid renders a submit button for products with one
 * variant, but the theme only ever binds its ajax handler through <wau-quick-add>,
 * which is the multi variant path. Unbound, the button posted the form and took the
 * shopper to the cart page. This binds the same ajax call the theme uses elsewhere,
 * delegated so carousels, infinite scroll and section reloads are covered.
 */
(function () {
  'use strict';

  // Only the single variant form: the multi variant disclosure binds its own
  // handler when it opens, and matching both would add the item twice.
  const BUTTON = '.product--grid_add button.quick--add_button.js-ajax-submit';

  function addToCart(button) {
    const form = button.closest('form');
    const config = document.getElementById('cart-config');
    const cart = window.Shopify && Shopify.theme && Shopify.theme.ajaxCart;

    // Without the ajax cart or its config the native submit is the better
    // outcome: the shopper still gets the item, by way of the cart page.
    if (!form || !config || !cart || typeof cart.addToCart !== 'function') return false;

    let cartConfig;
    try {
      cartConfig = JSON.parse(config.innerHTML || '{}');
    } catch (error) {
      return false;
    }

    button.classList.add('is-loading');
    cart.addToCart(form, form, cartConfig, false);
    window.setTimeout(function () { button.classList.remove('is-loading'); }, 1200);
    return true;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest && event.target.closest(BUTTON);
    if (!button) return;

    if (addToCart(button)) event.preventDefault();
  });
})();
